import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Cpu, ChevronLeft, ChevronRight,
  Plus, Pencil, Trash2, Save, X, RefreshCw, Copy, Search, GripVertical
} from 'lucide-react';
import { useDatabase } from '../../context/DatabaseContext';
import { useNotifications } from '../../context/NotificationContext';
import { integrationConfigApi } from '../../services/api';
import StuiSelect from '../../components/common/StuiSelect';

// ─── Empty form state ──────────────────────────────────────────────────────────

const EMPTY_PLATFORM = {
  platformCode: '',
  displayName: '',
  rawAiMin: '',
  rawAiMax: '',
  rawAoMin: '',
  rawAoMax: '',
  isVfd: false,
  defaultBaseTemplate: '',
  defaultAoiCode: '',
  channelPad: false,
  vfdDatatype: '',
};

const EMPTY_TEMPLATE = {
  ioType: '',
  templateData: '',
  templateCalibLow: '',
  templateCalibHigh: '',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const toSavePlatform = (f) => ({
  platformCode:        f.platformCode,
  displayName:         f.displayName,
  rawAiMin:            f.rawAiMin   !== '' ? parseFloat(f.rawAiMin)   : null,
  rawAiMax:            f.rawAiMax   !== '' ? parseFloat(f.rawAiMax)   : null,
  rawAoMin:            f.rawAoMin   !== '' ? parseFloat(f.rawAoMin)   : null,
  rawAoMax:            f.rawAoMax   !== '' ? parseFloat(f.rawAoMax)   : null,
  isVfd:               !!f.isVfd,
  defaultBaseTemplate: f.defaultBaseTemplate || null,
  defaultAoiCode:      f.defaultAoiCode      || null,
  channelPad:          !!f.channelPad,
  vfdDatatype:         f.vfdDatatype         || null,
});

const fromPlatform = (p) => ({
  platformCode:        p.platformCode        ?? '',
  displayName:         p.displayName         ?? '',
  rawAiMin:            p.rawAiMin            != null ? String(p.rawAiMin)   : '',
  rawAiMax:            p.rawAiMax            != null ? String(p.rawAiMax)   : '',
  rawAoMin:            p.rawAoMin            != null ? String(p.rawAoMin)   : '',
  rawAoMax:            p.rawAoMax            != null ? String(p.rawAoMax)   : '',
  isVfd:               !!p.isVfd,
  defaultBaseTemplate: p.defaultBaseTemplate ?? '',
  defaultAoiCode:      p.defaultAoiCode      ?? '',
  channelPad:          !!p.channelPad,
  vfdDatatype:         p.vfdDatatype         ?? '',
});

const toSaveTemplate = (f) => ({
  ioType:           f.ioType,
  templateData:     f.templateData,
  templateCalibLow: f.templateCalibLow  || null,
  templateCalibHigh: f.templateCalibHigh || null,
});

// ─── Component ────────────────────────────────────────────────────────────────

const PlcPlatformsPage = () => {
  const { dbSlug: slug } = useDatabase();
  const { notify } = useNotifications();

  const [platforms, setPlatforms] = useState([]);
  const [selected, setSelected]   = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [listCollapsed, setListCollapsed] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [menuOpen, setMenuOpen]     = useState(false);
  const menuRef = useRef(null);

  // Platform form
  const [form, setForm]       = useState(EMPTY_PLATFORM);
  const [isNew, setIsNew]     = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving]   = useState(false);

  // Template form (inline add)
  const [tmplForm, setTmplForm]         = useState(EMPTY_TEMPLATE);
  const [addingTemplate, setAddingTemplate] = useState(false);
  const [editingTmpl, setEditingTmpl]   = useState(null); // template id being edited
  const [editTmplForm, setEditTmplForm] = useState(EMPTY_TEMPLATE);
  const [tmplSaving, setTmplSaving]     = useState(false);

  // Copy-from modal
  const [showCopy, setShowCopy]             = useState(false);
  const [copySlugs, setCopySlugs]           = useState([]);
  const [copySourceSlug, setCopySourceSlug] = useState('');
  const [copyPlatforms, setCopyPlatforms]   = useState([]);
  const [copySelected, setCopySelected]     = useState([]);
  const [copySaving, setCopySaving]         = useState(false);

  // ── Click-outside for action menu ────────────────────────────────────────────

  useEffect(() => {
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Load ────────────────────────────────────────────────────────────────────

  const loadPlatforms = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    try {
      const res = await integrationConfigApi.getPlatforms(slug);
      setPlatforms(res.data);
    } catch (err) {
      console.error('Failed to load platforms:', err);
      notify.error('Failed to load platforms');
    } finally {
      setLoading(false);
    }
  }, [slug, notify]);

  useEffect(() => { loadPlatforms(); }, [loadPlatforms]);

  const loadTemplates = useCallback(async (platformId) => {
    try {
      const res = await integrationConfigApi.getTemplates(slug, platformId);
      setTemplates(res.data);
    } catch (err) {
      console.error('Failed to load templates:', err);
    }
  }, [slug]);

  // ── Selection ───────────────────────────────────────────────────────────────

  const handleSelect = (platform) => {
    setSelected(platform);
    setForm(fromPlatform(platform));
    setEditing(false);
    setIsNew(false);
    setAddingTemplate(false);
    setEditingTmpl(null);
    loadTemplates(platform.id);
  };

  // ── Platform CRUD ───────────────────────────────────────────────────────────

  const handleNew = () => {
    setSelected(null);
    setTemplates([]);
    setForm(EMPTY_PLATFORM);
    setIsNew(true);
    setEditing(true);
    setAddingTemplate(false);
    setEditingTmpl(null);
  };

  const handleCancel = () => {
    if (selected) {
      setForm(fromPlatform(selected));
      setEditing(false);
      setIsNew(false);
    } else {
      setIsNew(false);
      setEditing(false);
      setForm(EMPTY_PLATFORM);
    }
    // Inline template add/edit rows commit immediately — they must not outlive edit mode (#115)
    setAddingTemplate(false);
    setTmplForm(EMPTY_TEMPLATE);
    setEditingTmpl(null);
    setEditTmplForm(EMPTY_TEMPLATE);
  };

  const handleSave = async () => {
    if (!form.platformCode.trim() || !form.displayName.trim()) {
      notify.warning('Platform Code and Display Name are required');
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const res = await integrationConfigApi.createPlatform(slug, toSavePlatform(form));
        const created = res.data;
        setPlatforms(prev => [...prev, created].sort((a, b) => a.displayName.localeCompare(b.displayName)));
        setSelected(created);
        setForm(fromPlatform(created));
        setIsNew(false);
        setEditing(false);
        setTemplates([]);
        notify.success(`Platform "${created.displayName}" created`);
      } else {
        const res = await integrationConfigApi.updatePlatform(slug, selected.id, toSavePlatform(form));
        const updated = res.data;
        setPlatforms(prev => prev.map(p => p.id === updated.id ? updated : p));
        setSelected(updated);
        setForm(fromPlatform(updated));
        setEditing(false);
        notify.success('Platform saved');
      }
    } catch (err) {
      console.error('Save failed:', err);
      notify.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    if (!window.confirm(`Delete platform "${selected.displayName}" and all its templates?`)) return;
    try {
      await integrationConfigApi.deletePlatform(slug, selected.id);
      setPlatforms(prev => prev.filter(p => p.id !== selected.id));
      setSelected(null);
      setTemplates([]);
      setForm(EMPTY_PLATFORM);
      setEditing(false);
      notify.success('Platform deleted');
    } catch (err) {
      notify.error('Delete failed');
    }
  };

  const handleFieldChange = (key, val) => setForm(f => ({ ...f, [key]: val }));

  // ── Template CRUD ────────────────────────────────────────────────────────────

  const handleAddTemplate = async () => {
    if (!editing) return; // a leaked add-row must never be able to write (#115)
    if (!tmplForm.ioType.trim() || !tmplForm.templateData.trim()) {
      notify.warning('IO Type and Template Data are required');
      return;
    }
    setTmplSaving(true);
    try {
      const res = await integrationConfigApi.addTemplate(slug, selected.id, toSaveTemplate(tmplForm));
      setTemplates(prev => [...prev, res.data]);
      setTmplForm(EMPTY_TEMPLATE);
      setAddingTemplate(false);
      notify.success('Template added');
    } catch (err) {
      notify.error('Failed to add template');
    } finally {
      setTmplSaving(false);
    }
  };

  const handleStartEditTmpl = (tmpl) => {
    setEditingTmpl(tmpl.id);
    setEditTmplForm({
      ioType:            tmpl.ioType,
      templateData:      tmpl.templateData,
      templateCalibLow:  tmpl.templateCalibLow  ?? '',
      templateCalibHigh: tmpl.templateCalibHigh ?? '',
    });
  };

  const handleSaveTmpl = async (tmplId) => {
    setTmplSaving(true);
    try {
      const res = await integrationConfigApi.updateTemplate(slug, selected.id, tmplId, toSaveTemplate(editTmplForm));
      setTemplates(prev => prev.map(t => t.id === tmplId ? res.data : t));
      setEditingTmpl(null);
      notify.success('Template saved');
    } catch (err) {
      notify.error('Failed to save template');
    } finally {
      setTmplSaving(false);
    }
  };

  const handleDeleteTmpl = async (tmplId) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await integrationConfigApi.deleteTemplate(slug, selected.id, tmplId);
      setTemplates(prev => prev.filter(t => t.id !== tmplId));
      notify.success('Template deleted');
    } catch (err) {
      notify.error('Failed to delete template');
    }
  };

  // ── Copy-from ────────────────────────────────────────────────────────────────

  const openCopyModal = async () => {
    try {
      const res = await integrationConfigApi.getSlugs();
      setCopySlugs(res.data.filter(s => s !== slug));
      setCopySourceSlug('');
      setCopyPlatforms([]);
      setCopySelected([]);
      setShowCopy(true);
    } catch (err) {
      notify.error('Failed to load slugs');
    }
  };

  const handleCopySlugChange = async (sourceSlug) => {
    setCopySourceSlug(sourceSlug);
    setCopyPlatforms([]);
    setCopySelected([]);
    if (!sourceSlug) return;
    try {
      const res = await integrationConfigApi.getPlatformsFrom(slug, sourceSlug);
      setCopyPlatforms(res.data);
    } catch (err) {
      notify.error('Failed to load source platforms');
    }
  };

  const handleExecuteCopy = async () => {
    if (!copySelected.length) return;
    setCopySaving(true);
    try {
      await integrationConfigApi.copyPlatforms(slug, { sourceSlug: copySourceSlug, platformIds: copySelected });
      await loadPlatforms();
      setShowCopy(false);
      notify.success(`Copied ${copySelected.length} platform(s)`);
    } catch (err) {
      notify.error('Copy failed');
    } finally {
      setCopySaving(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  const isDirty = editing;

  const q = searchTerm.toLowerCase();
  const filteredPlatforms = q
    ? platforms.filter(p =>
        p.displayName?.toLowerCase().includes(q) ||
        p.platformCode?.toLowerCase().includes(q)
      )
    : platforms;

  return (
    <div className="stui-module-container">

      {/* HEADER */}
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <Cpu size={20} />
          <h1>PLC Platforms</h1>
          <span className="stui-module-header-meta">{platforms.length} platform{platforms.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="stui-module-header-actions">
          <button className="stui-crud-btn" onClick={handleNew} title="Add New Record"><Plus size={18} /></button>
          <button onClick={editing ? handleCancel : () => setEditing(true)} className={`stui-crud-btn ${!editing && !selected ? 'disabled' : ''}`} disabled={!editing && !selected} title={editing ? 'Cancel' : 'Edit Record'}>
            {editing ? <X size={18} /> : <Pencil size={18} />}
          </button>
          <button className={`stui-crud-btn ${!isDirty ? 'disabled' : ''}`} onClick={handleSave} disabled={!isDirty || saving} title="Save Changes"><Save size={18} /></button>
          <button className={`stui-crud-btn ${!selected || editing ? 'disabled' : ''}`} onClick={handleDelete} disabled={!selected || editing} title="Delete Record"><Trash2 size={18} /></button>
          <div className="stui-action-menu" ref={menuRef}>
            <button className="stui-action-menu-btn" onClick={() => setMenuOpen(o => !o)} title="Actions">
              <GripVertical size={16} />
            </button>
            {menuOpen && (
              <div className="stui-action-menu-dropdown">
                <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); loadPlatforms(); }}>
                  <RefreshCw size={16} /><span>Refresh</span>
                </button>
                <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); openCopyModal(); }}>
                  <Copy size={16} /><span>Copy from Database…</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* LIST + DETAIL */}
      <div className="stui-list-view">

        {/* LIST PANEL */}
        {listCollapsed ? (
          <div className="stui-list-panel stui-collapsed">
            <button onClick={() => setListCollapsed(false)} className="stui-list-panel-expand-btn" title="Expand list"><ChevronRight size={16} /></button>
          </div>
        ) : (
          <div className="stui-list-panel">
            <div className="stui-list-panel-header">
              <button onClick={() => setListCollapsed(true)} className="stui-list-panel-collapse-btn" title="Collapse list"><ChevronLeft size={16} /></button>
              <div className="stui-search-wrapper">
                <Search size={14} className="stui-search-icon" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  placeholder="Search platforms..."
                  className="stui-field-input"
                />
              </div>
            </div>
            <div className="stui-list-scroll">
              {loading ? (
                <div className="stui-list-empty">Loading...</div>
              ) : filteredPlatforms.length === 0 ? (
                <div className="stui-list-empty">{platforms.length === 0 ? 'No platforms' : 'No matches'}</div>
              ) : filteredPlatforms.map(p => (
                <button
                  key={p.id}
                  onClick={() => handleSelect(p)}
                  className={`stui-list-item ${selected?.id === p.id ? 'selected' : ''}`}
                >
                  <span className="stui-list-item-primary">{p.displayName}</span>
                  <span className="stui-list-item-secondary">{p.platformCode}{p.isVfd ? ' · VFD' : ''}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* DETAIL PANEL */}
        <div className="stui-detail-panel">
          {(selected || isNew) ? (
            <div className="stui-detail-content">

              {/* PLATFORM FIELDS */}
              <div className="stui-detail-grid">
                <div className="stui-col-24"><div className="stui-detail-section-header">Platform</div></div>

                <div className="stui-field-group stui-col-6">
                  <label>Platform Code</label>
                  <input className="stui-field-input" value={form.platformCode} onChange={e => handleFieldChange('platformCode', e.target.value)} readOnly={!editing} />
                </div>
                <div className="stui-field-group stui-col-10">
                  <label>Display Name</label>
                  <input className="stui-field-input" value={form.displayName} onChange={e => handleFieldChange('displayName', e.target.value)} readOnly={!editing} />
                </div>
                <div className="stui-field-group stui-col-4">
                  <label title="Only required for VFDs — used to generate the AOI call in the program">Default AOI Code</label>
                  <input className="stui-field-input" value={form.defaultAoiCode} onChange={e => handleFieldChange('defaultAoiCode', e.target.value)} readOnly={!editing} />
                </div>
                <div className="stui-field-group stui-col-4">
                  <label title="Must match how the remote IO panels are named in the PLC code (e.g. LOCAL:1, RIO_1)">Default Base Template</label>
                  <input className="stui-field-input" value={form.defaultBaseTemplate} onChange={e => handleFieldChange('defaultBaseTemplate', e.target.value)} readOnly={!editing} />
                </div>

                <div className="stui-col-24"><div className="stui-detail-section-header">Raw Ranges (IOAIS/IOAOS)</div></div>

                <div className="stui-field-group stui-col-3">
                  <label title="Raw integer minimum output from the PLC analog input card (e.g. 0 or 3277)">AI Min</label>
                  <input className="stui-field-input" type="number" value={form.rawAiMin} onChange={e => handleFieldChange('rawAiMin', e.target.value)} readOnly={!editing} />
                </div>
                <div className="stui-field-group stui-col-3">
                  <label title="Raw integer maximum output from the PLC analog input card (e.g. 16383 or 32767)">AI Max</label>
                  <input className="stui-field-input" type="number" value={form.rawAiMax} onChange={e => handleFieldChange('rawAiMax', e.target.value)} readOnly={!editing} />
                </div>
                <div className="stui-field-group stui-col-3">
                  <label title="Raw integer minimum sent to the PLC analog output card">AO Min</label>
                  <input className="stui-field-input" type="number" value={form.rawAoMin} onChange={e => handleFieldChange('rawAoMin', e.target.value)} readOnly={!editing} />
                </div>
                <div className="stui-field-group stui-col-3">
                  <label title="Raw integer maximum sent to the PLC analog output card">AO Max</label>
                  <input className="stui-field-input" type="number" value={form.rawAoMax} onChange={e => handleFieldChange('rawAoMax', e.target.value)} readOnly={!editing} />
                </div>

                <div className="stui-col-24"><div className="stui-detail-section-header">Options</div></div>

                <div className="stui-field-group stui-col-4">
                  <label title="Pad channel numbering to maintain consistent slot addressing across mixed module types">Channel Pad</label>
                  <div className="stui-checkbox">
                    <input type="checkbox" className="stui-checkbox-input" checked={form.channelPad} onChange={e => handleFieldChange('channelPad', e.target.checked)} disabled={!editing} />
                  </div>
                </div>
                <div className="stui-field-group stui-col-4">
                  <label title="Enables VFD-specific output generation — drive tags and network IO handling">Is VFD</label>
                  <div className="stui-checkbox">
                    <input type="checkbox" className="stui-checkbox-input" checked={form.isVfd} onChange={e => handleFieldChange('isVfd', e.target.checked)} disabled={!editing} />
                  </div>
                </div>
                <div className="stui-field-group stui-col-6">
                  <label title="PLC datatype used for the VFD drive tag (e.g. POWERFLEX755_DRIVE)">VFD Datatype</label>
                  <input className="stui-field-input" value={form.vfdDatatype} onChange={e => handleFieldChange('vfdDatatype', e.target.value)} readOnly={!editing || !form.isVfd} placeholder={form.isVfd ? 'e.g. POWERFLEX755_DRIVE' : 'Enable Is VFD to set'} />
                </div>

              </div>

              {/* REGISTER TEMPLATES SUB-TABLE */}
              {selected && !isNew && (
                <div className="stui-inner-table-section">
                  <div className="stui-detail-section-header stui-inner-table-section-header">
                    <span>Register Templates</span>
                    <button className="stui-btn stui-btn-secondary stui-btn-sm" onClick={() => setAddingTemplate(true)} title="Add template">
                      <Plus size={12} /> Add
                    </button>
                  </div>

                  <table className="stui-inner-table">
                    <thead>
                      <tr>
                        <th>IO Type</th>
                        <th>Template Data</th>
                        <th className="stui-inner-table-col-sm">Calib Low</th>
                        <th className="stui-inner-table-col-sm">Calib High</th>
                        <th className="stui-inner-table-col-actions"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {templates.map(t => (
                        <tr key={t.id} className={editingTmpl === t.id ? 'stui-inner-table-edit-row' : ''}>
                          {editingTmpl === t.id ? (
                            <>
                              <td><input className="stui-field-input stui-inner-table-input-xs" value={editTmplForm.ioType} onChange={e => setEditTmplForm(f => ({ ...f, ioType: e.target.value }))} /></td>
                              <td><input className="stui-field-input" value={editTmplForm.templateData} onChange={e => setEditTmplForm(f => ({ ...f, templateData: e.target.value }))} /></td>
                              <td><input className="stui-field-input stui-inner-table-input-sm" value={editTmplForm.templateCalibLow} onChange={e => setEditTmplForm(f => ({ ...f, templateCalibLow: e.target.value }))} /></td>
                              <td><input className="stui-field-input stui-inner-table-input-sm" value={editTmplForm.templateCalibHigh} onChange={e => setEditTmplForm(f => ({ ...f, templateCalibHigh: e.target.value }))} /></td>
                              <td className="stui-inner-table-cell-actions">
                                <button className="stui-crud-btn" onClick={() => handleSaveTmpl(t.id)} disabled={tmplSaving} title="Save"><Save size={13} /></button>
                                <button className="stui-crud-btn" onClick={() => setEditingTmpl(null)} title="Cancel"><X size={13} /></button>
                              </td>
                            </>
                          ) : (
                            <>
                              <td>{t.ioType}</td>
                              <td className="stui-inner-table-cell-mono">{t.templateData}</td>
                              <td className="stui-inner-table-cell-mono">{t.templateCalibLow ?? '—'}</td>
                              <td className="stui-inner-table-cell-mono">{t.templateCalibHigh ?? '—'}</td>
                              <td className="stui-inner-table-cell-actions">
                                <button className="stui-crud-btn" onClick={() => handleStartEditTmpl(t)} title="Edit"><Pencil size={13} /></button>
                                <button className="stui-crud-btn" onClick={() => handleDeleteTmpl(t.id)} title="Delete"><Trash2 size={13} /></button>
                              </td>
                            </>
                          )}
                        </tr>
                      ))}

                      {editing && addingTemplate && (
                        <tr className="stui-inner-table-edit-row stui-inner-table-add-row">
                          <td><input className="stui-field-input stui-inner-table-input-xs" placeholder="AI" value={tmplForm.ioType} onChange={e => setTmplForm(f => ({ ...f, ioType: e.target.value }))} /></td>
                          <td><input className="stui-field-input" placeholder="Template string..." value={tmplForm.templateData} onChange={e => setTmplForm(f => ({ ...f, templateData: e.target.value }))} /></td>
                          <td><input className="stui-field-input stui-inner-table-input-sm" placeholder="Calib low" value={tmplForm.templateCalibLow} onChange={e => setTmplForm(f => ({ ...f, templateCalibLow: e.target.value }))} /></td>
                          <td><input className="stui-field-input stui-inner-table-input-sm" placeholder="Calib high" value={tmplForm.templateCalibHigh} onChange={e => setTmplForm(f => ({ ...f, templateCalibHigh: e.target.value }))} /></td>
                          <td className="stui-inner-table-cell-actions">
                            <button className="stui-crud-btn" onClick={handleAddTemplate} disabled={tmplSaving} title="Add"><Save size={13} /></button>
                            <button className="stui-crud-btn" onClick={() => { setAddingTemplate(false); setTmplForm(EMPTY_TEMPLATE); }} title="Cancel"><X size={13} /></button>
                          </td>
                        </tr>
                      )}

                      {templates.length === 0 && !addingTemplate && (
                        <tr className="stui-inner-table-empty"><td colSpan={5}>No templates — click + to add</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

            </div>
          ) : (
            <div className="stui-detail-empty">
              <Cpu size={48} />
              <p>Select a platform or click + to create one</p>
            </div>
          )}
        </div>
      </div>

      {/* COPY-FROM MODAL */}
      {showCopy && (
        <div className="stui-modal-overlay" onClick={() => setShowCopy(false)}>
          <div className="stui-modal" onClick={e => e.stopPropagation()}>
            <div className="stui-modal-header">
              <h2 className="stui-modal-title">Copy Platforms From Another Database</h2>
              <button className="stui-modal-close" onClick={() => setShowCopy(false)}><X size={18} /></button>
            </div>
            <div className="stui-modal-body">
              <div className="stui-field-group">
                <label>Source Database</label>
                <StuiSelect className="stui-field-input" value={copySourceSlug} onChange={e => handleCopySlugChange(e.target.value)}>
                  <option value="">— Select —</option>
                  {copySlugs.map(s => <option key={s} value={s}>{s}</option>)}
                </StuiSelect>
              </div>
              {copyPlatforms.length > 0 && (
                <div>
                  <label className="stui-modal-checklist-label">
                    Select platforms to copy (templates included):
                  </label>
                  <div className="stui-modal-checklist">
                    {copyPlatforms.map(p => (
                      <label key={p.id} className="stui-modal-checklist-item">
                        <input
                          type="checkbox"
                          className="stui-checkbox-input"
                          checked={copySelected.includes(p.id)}
                          onChange={e => setCopySelected(prev => e.target.checked ? [...prev, p.id] : prev.filter(id => id !== p.id))}
                        />
                        <span className="stui-label">{p.displayName}</span>
                        <span className="stui-sublabel">{p.platformCode}{p.isVfd ? ' · VFD' : ''}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="stui-modal-footer">
              <button className="stui-btn stui-btn-secondary" onClick={() => setShowCopy(false)}>Cancel</button>
              <button className="stui-btn stui-btn-primary" onClick={handleExecuteCopy} disabled={!copySelected.length || copySaving}>
                {copySaving ? 'Copying...' : `Copy ${copySelected.length || ''} Platform${copySelected.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlcPlatformsPage;
